const processedStatus = `data?.status == 'PROCESSED'`;
const openStatus = `data?.status == 'OPEN'`;
const completelyDeliveredStatus = "data?.weight == data?.deliveredWeight";
const partiallyDeliveredStatus = "data?.deliveredWeight > 0";
const blockedStatus = "data?.status == 'BLOCKED'";
const enRouteStatus = `data?.status == 'EN_ROUTE'`;
const inProgressStatus = `data?.status == 'EXPECTED'`;
const syncingStatus = `data?.status == "SYNCING_WITH_ACCOUNTING_SOFTWARE"`;
const syncFailedStatus = `data?.status == "SYNCING_FAILED"`;
const syncedStatus = `data?.status == "SYNCED"`;
const usedStatus = "data?.status == 'USED'";
const reservedStatus = "data?.status == 'RESERVED'";
const partiallyReservedStatus = "data?.status == 'PARTIALLY_RESERVED'";
const confirmedStatus = "data?.status == 'BILLED'";
const fullyReservedStatus = "Number(data?.packageQuantity) > 0 && Number(data?.saldoPackageQuantity) === 0";

const billedColor = "bg-green-400";
const closedColor = "bg-green-200";
const inProcessColor = "bg-amber-400";
const inProductionColor = "bg-amber-200";
const conceptColor = "bg-stone-200";
const openColor = "bg-white";
const blockedColor = "bg-red-200";
const enRouteColor = "bg-orange-300";
const inProgressColor = "bg-amber-200";
const syncingColor = "bg-violet-300";
const syncedColor = "bg-blue-300";
const usedColor = "bg-blue-200";
const fullyReservedColor = "bg-emerald-200";

export const purchaseOrderStatusColors: Record<string, string> = {
	SYNCING_FAILED: blockedColor,
	SYNCING_WITH_ACCOUNTING_SOFTWARE: syncingColor,
	SYNCED: syncedColor,
	CLOSED: closedColor,
	IN_PROCESS: inProcessColor,
	CONCEPT: conceptColor,
	OPEN: openColor,
};

export const productionStatusColors: Record<string, string> = {
	SYNCING_FAILED: blockedColor,
	SYNCING_WITH_ACCOUNTING_SOFTWARE: syncingColor,
	SYNCED: syncedColor,
	BILLED: billedColor,
	CLOSED: closedColor,
	IN_PROCESS: inProcessColor,
	IN_PRODUCTION: inProductionColor,
	CONCEPT: conceptColor,
	OPEN: openColor,
};

export const standardStatusColors: Record<string, string> = {
	BILLED: billedColor,
	CLOSED: closedColor,
	IN_PROCESS: inProcessColor,
	IN_PRODUCTION: inProductionColor,
	CONCEPT: conceptColor,
	OPEN: openColor,
};

function statusRules(colors: Record<string, string>): Record<string, string> {
	const rules: Record<string, string> = {};
	for (const [status, color] of Object.entries(colors)) {
		const condition = `data?.status == '${status}'`;
		rules[color] = rules[color] ? `${rules[color]} || ${condition}` : condition;
	}
	return rules;
}

function purchaseOrderStatusColor() {
	return statusRules(purchaseOrderStatusColors);
}

function purchaseOrderRowStatusColor() {
	return statusRules(standardStatusColors);
}

function salesOrderStatusColor() {
	return statusRules(standardStatusColors);
}

function salesOrderRowStatusColor() {
	return statusRules(standardStatusColors);
}

function salesOrderRowIndexStatusColor() {
	return {
		...statusRules(standardStatusColors),
		[fullyReservedColor]: fullyReservedStatus,
	};
}

function productionStatusColor() {
	return statusRules(productionStatusColors);
}

function productionStepStatusColor() {
	return statusRules(standardStatusColors);
}

function inboundStatusColor() {
	return {
		[closedColor]: completelyDeliveredStatus,
		[inProcessColor]: partiallyDeliveredStatus,
	};
}

function outboundStatusColor() {
	return {
		[closedColor]: completelyDeliveredStatus,
		[inProcessColor]: partiallyDeliveredStatus,
	};
}

function lotStatusColor() {
	return {
		[blockedColor]: blockedStatus,
		[usedColor]: usedStatus,
		[fullyReservedColor]: reservedStatus,
		[inProcessColor]: partiallyReservedStatus,
		[inProgressColor]: inProgressStatus,
		[enRouteColor]: enRouteStatus,
	};
}

function productionStepResultLotStatusColor() {
	const producedPackages = (data: any) =>
		(data?.resultLots ?? []).reduce((sum: number, lot: any) => sum + Number(lot?.packageQuantity ?? 0), 0);

	const isClosed = (data: any) => {
		if (data?.status == "CLOSED") return true;
		const target = Number(data?.packageQuantity ?? 0);
		return target > 0 && producedPackages(data) >= target;
	};

	return {
		[closedColor]: (params: { data?: any }) => isClosed(params.data),
		[inProductionColor]: (params: { data?: any }) =>
			!isClosed(params.data) &&
			(params.data?.status == "OPEN" ||
				params.data?.salesOrderRow?.status == "IN_PRODUCTION" ||
				producedPackages(params.data) > 0),
	};
}

function reservationStatusColor() {
	return {
		[closedColor]: processedStatus,
		[openColor]: openStatus,
	};
}

function invoiceStatusColor() {
	return {
		[blockedColor]: syncFailedStatus,
		[syncingColor]: syncingStatus,
		[syncedColor]: syncedStatus,
		[closedColor]: confirmedStatus,
	};
}

export {
	purchaseOrderStatusColor,
	purchaseOrderRowStatusColor,
	salesOrderStatusColor,
	salesOrderRowStatusColor,
	salesOrderRowIndexStatusColor,
	productionStatusColor,
	productionStepStatusColor,
	inboundStatusColor,
	outboundStatusColor,
	lotStatusColor,
	productionStepResultLotStatusColor,
	reservationStatusColor,
	invoiceStatusColor,
};
